import { FC } from 'react';

const ServicesPage: FC = () => {
  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center">Our Services</h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Comprehensive AI-enhanced marketing and business development solutions for financial service providers
          </p>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="text-3xl font-bold mb-6">Transforming Financial Marketing</h2>
              <p className="mb-4">
                DLDV Enterprises offers a comprehensive suite of services designed specifically for investment firms and financial service providers. Our solutions leverage cutting-edge AI and automation to optimize every aspect of your marketing and business development efforts.
              </p>
              <p className="mb-4">
                Whether you're looking to streamline your capital raising process, enhance your marketing materials, or implement advanced AI solutions, our team combines deep industry expertise with technological innovation to deliver exceptional results.
              </p>
              <p>
                Each service can be tailored to your specific needs and integrated seamlessly with your existing processes, ensuring maximum impact with minimal disruption.
              </p>
            </div>
            <div className="flex justify-center">
              <img src="/images/dldv.svg" alt="DLDV Enterprises" className="w-full max-w-md" />
            </div>
          </div>
        </div>
      </section>

      {/* Placement Agent Services */}
      <section className="section bg-gray-50" id="placement-agent">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">Placement Agent Services</h2>
              <p className="mb-6">
                Our AI-enhanced placement agent services connect investment firms with qualified investors, streamlining the capital raising process and improving conversion rates.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">AI-Powered Investor Matching:</span> Sophisticated algorithms identify investors most likely to be interested in your offering
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Personalized Outreach:</span> Customized communication strategies for each investor segment
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Comprehensive Investor Database:</span> Access to a curated network of qualified investors
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Due Diligence Support:</span> Streamlined processes for information sharing and verification
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Regulatory Compliance:</span> Ensuring all placement activities adhere to relevant regulations
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Faster capital raising with higher conversion rates</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Access to investors who are genuinely interested in your strategy</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Reduced time and resources spent on unqualified prospects</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Data-driven insights to continuously improve fundraising strategies</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6">
                "DLDV's placement agent services helped us raise our target capital in half the time we expected. Their AI-powered investor matching was remarkably accurate, connecting us with investors who were genuinely interested in our strategy."
              </p>
              <p className="text-right font-medium">- Managing Partner, Private Equity Firm</p>
              
              <div className="mt-8 text-center">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="text-accent-color hover:underline">Contact Us to Learn More →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Discuss Your Needs</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Network Introductions */}
      <section className="section" id="network-introductions">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">Network Introductions</h2>
              <p className="mb-6">
                Leverage our extensive network and AI-powered matching algorithms to connect with key stakeholders in the financial services industry, from potential investors to strategic partners.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Strategic Relationship Mapping:</span> Identify the most valuable connections for your specific objectives
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Warm Introductions:</span> Facilitated connections through established relationships
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Relationship Intelligence:</span> AI-powered insights on optimal approach strategies
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Follow-up Automation:</span> Systematic nurturing of new connections
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Access to otherwise inaccessible decision-makers</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Higher response and engagement rates through warm introductions</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Accelerated business development through strategic partnerships</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Expanded network with quality over quantity approach</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6 text-justify">
                "The strategic introductions facilitated by DLDV led to three major institutional partnerships that transformed our distribution capabilities. Their relationship intelligence approach ensured we connected with the right decision-makers."
              </p>
              <p className="text-right font-medium">- CEO, Asset Management Firm</p>
              
              <div className="mt-8 text-center">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="text-accent-color hover:underline">Contact Us to Learn More →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Expand Your Network</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Hosting & Management */}
      <section className="section bg-gray-50" id="event-hosting">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">Event Hosting & Management</h2>
              <p className="mb-6 text-justify">
                From intimate investor dinners to large-scale conferences, our AI-enhanced event management services ensure maximum impact and ROI for your marketing events.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Strategic Event Planning:</span> Data-driven approach to event format, timing, and content
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">AI-Powered Attendee Curation:</span> Identifying and attracting the most valuable participants
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">End-to-End Management:</span> Comprehensive handling of logistics, content, and follow-up
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Virtual and Hybrid Options:</span> Leveraging technology for maximum reach and engagement
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">ROI Measurement:</span> Sophisticated analytics to track event performance and outcomes
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Higher-quality attendees and more meaningful connections</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Reduced internal resource requirements for event management</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Enhanced brand positioning through professional execution</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Measurable outcomes and clear return on investment</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6 text-justify">
                "DLDV transformed our annual investor conference from a resource-intensive obligation to a strategic asset. Their AI-powered attendee curation brought exactly the right people into the room, resulting in multiple new investment commitments."
              </p>
              <p className="text-right font-medium">- Investor Relations Director, Hedge Fund</p>
              
              <div className="mt-8 text-center">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="text-accent-color hover:underline">Contact Us to Learn More →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Plan Your Next Event</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Document Creation */}
      <section className="section" id="document-creation">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">Document Creation</h2>
              <p className="mb-6 text-justify">
                Our AI-powered document creation services produce compelling, compliant, and customized marketing materials for financial service providers, from pitch decks to investment memoranda.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">AI-Powered Content Generation:</span> Sophisticated algorithms that create compelling, accurate content
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Professional Design:</span> Visually stunning layouts that enhance your brand
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Compliance Integration:</span> Automated regulatory checks and disclosures
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Dynamic Personalization:</span> Tailored content for different investor segments
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Rapid Turnaround:</span> Significantly faster production compared to traditional methods
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Dramatic reduction in document creation time and cost</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Consistent messaging and branding across all materials</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Higher engagement rates through personalized content</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Reduced compliance risk through automated regulatory checks</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6 text-justify">
                "The AI-powered document creation service from DLDV has revolutionized how we produce marketing materials. What used to take weeks now takes days, with better quality and consistency. Our compliance team is particularly impressed with the automated regulatory checks."
              </p>
              <p className="text-right font-medium">- Marketing Director, Asset Management Firm</p>
              
              <div className="mt-8 text-center">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="text-accent-color hover:underline">Contact Us to Learn More →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Transform Your Documents</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Development */}
      <section className="section bg-gray-50" id="website-development">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">Website Development</h2>
              <p className="mb-6 text-justify">
                Our specialized website development services create high-performing, compliant, and engaging digital experiences for financial service providers.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Industry-Specific Design:</span> Websites tailored for financial service providers
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Compliance-First Approach:</span> Built with regulatory requirements in mind
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Secure Investor Portals:</span> Protected access for clients and prospects
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">AI-Enhanced Content:</span> Dynamic, personalized content for visitors
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Performance Analytics:</span> Sophisticated tracking and optimization tools
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Professional digital presence that builds credibility and trust</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Secure platform for investor communications and document sharing</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Higher engagement through personalized user experiences</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Reduced compliance risk with built-in regulatory safeguards</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6 text-justify">
                "Our new website developed by DLDV has transformed our digital presence. The secure investor portal has been particularly valuable, streamlining our communications and document sharing while maintaining the highest security standards."
              </p>
              <p className="text-right font-medium">- CTO, Investment Management Firm</p>
              
              <div className="mt-8 text-center">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="text-accent-color hover:underline">Contact Us to Learn More →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Upgrade Your Web Presence</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI & Automation Integration */}
      <section className="section" id="ai-automation-integration">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-bold mb-6">AI & Automation Integration</h2>
              <p className="mb-6 text-justify">
                Transform your marketing and business development operations with custom AI and automation solutions designed specifically for financial service providers.
              </p>
              
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Key Features</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Custom AI Solutions:</span> Tailored to your specific business challenges
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Workflow Automation:</span> Streamlining repetitive marketing and sales processes
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Predictive Analytics:</span> Data-driven insights for strategic decision-making
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Seamless Integration:</span> Works with your existing systems and processes
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>
                      <span className="font-bold">Continuous Optimization:</span> AI models that learn and improve over time
                    </div>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3">Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Dramatic efficiency improvements in marketing operations</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Enhanced decision-making through data-driven insights</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Scalable marketing capabilities without proportional cost increases</div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-accent-color mr-3 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                    <div>Competitive advantage through cutting-edge technology adoption</div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="card p-6">
              <h3 className="text-2xl font-bold mb-4 text-center">Success Story</h3>
              <p className="italic mb-6 text-justify">
                "The AI and automation solutions implemented by DLDV have transformed our marketing department. We've seen a 70% reduction in time spent on routine tasks, allowing our team to focus on strategy and relationship building."
              </p>
              <p className="text-right font-medium">- Head of Marketing, Private Equity Firm</p>
              
              <div className="mt-8 text-center">
                <a href="/ai-automation" className="text-accent-color hover:underline">Learn More About Our AI Solutions →</a>
              </div>
              
              <div className="mt-8">
                <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary w-full text-center">Explore AI Integration</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="section bg-primary-color text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Financial Marketing?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contact us today to discuss how our AI-enhanced services can help your financial firm achieve exceptional results.
          </p>
          <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary bg-white text-primary-color hover:bg-gray-100">Schedule a Consultation</a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;